import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  Alert, Avatar, Box, Button, Chip, Divider, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import PageContainer from '../components/PageContainer';
import { useIsXs } from '../hooks/useMobileLayout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelIcon from '@mui/icons-material/Cancel';
import { fmtCurrency, FEE_AGREEMENT_LABEL } from '../brand';
import type { Fee, Payment } from '../../types/domain';

interface LocationState { payment?: Payment; fee?: Fee | null }

export default function MercadoPagoMock() {
  const isXs = useIsXs();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;
  const payment = state.payment;
  const fee = state.fee;

  const [phase, setPhase] = useState<'redirect' | 'processing' | 'result'>('redirect');

  useEffect(() => {
    if (!payment) return;
    const t1 = setTimeout(() => setPhase('processing'), 700);
    const t2 = setTimeout(() => setPhase('result'), 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [payment]);

  const colorByStatus = useMemo(() => {
    switch (payment?.status) {
      case 'APPROVED': return 'success' as const;
      case 'PENDING':  return 'warning' as const;
      case 'REJECTED': return 'error' as const;
      default:         return 'default' as const;
    }
  }, [payment?.status]);

  if (!payment) {
    return (
      <PageContainer maxWidth="sm" sx={{ py: 2 }}>
        <Alert severity="error" sx={{ mt: { sm: 4 } }}>
          No se recibieron los datos del pago. <Button onClick={() => navigate('/home')}>Volver al inicio</Button>
        </Alert>
      </PageContainer>
    );
  }

  const statusIcon = payment.status === 'APPROVED'
    ? <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
    : payment.status === 'PENDING'
      ? <HourglassTopIcon sx={{ fontSize: 64, color: 'warning.main' }} />
      : <CancelIcon sx={{ fontSize: 64, color: 'error.main' }} />;

  const statusLabel = payment.status === 'APPROVED'
    ? 'Pago aprobado'
    : payment.status === 'PENDING'
      ? 'Pago pendiente'
      : 'Pago rechazado';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <PageContainer maxWidth="sm" sx={{ py: 3 }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }} variant="outlined">
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, minWidth: 0 }}>
            <Avatar sx={{ bgcolor: 'grey.500', color: 'grey.100', fontSize: 14 }}>MP</Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Mercado Pago — Simulación</Typography>
              <Typography variant="caption" color="text.secondary">Pasarela de pruebas (no se realiza un cargo real).</Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {phase !== 'result' && (
            <Stack spacing={2} sx={{ py: 4, alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {phase === 'redirect' ? 'Redireccionando al checkout...' : 'Procesando pago...'}
              </Typography>
              <LinearProgress sx={{ width: '100%' }} />
              <Typography variant="caption" color="text.secondary">Referencia: <strong>{payment.simulatedReference}</strong></Typography>
            </Stack>
          )}

          {phase === 'result' && (
            <Stack spacing={2} sx={{ py: 2, alignItems: 'center', textAlign: 'center' }}>
              {statusIcon}
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{statusLabel}</Typography>
              <Chip variant="outlined" color={colorByStatus} label={payment.status} size="small" />
              <Box sx={{ width: '100%', mt: 2 }}>
                <Stack spacing={1}>
                  <Row label="Concepto" value={fee?.name || (payment.feeCode === 'AGREEMENT' ? FEE_AGREEMENT_LABEL : 'Sin Acuerdo')} />
                  <Row label="Documento" value={`${payment.documentType === 'PASSPORT' ? 'Pasaporte' : 'DNI'} ${payment.documentNumber}`} />
                  <Row label="Importe" value={fmtCurrency(payment.amount, payment.currency)} bold />
                  <Row label="Empresa" value={payment.tenantName || `#${payment.tenantId}`} />
                  {payment.sindicatoName && (
                    <Row label="CAMARAS" value={payment.sindicatoName} />
                  )}
                  <Row label="Referencia" value={payment.simulatedReference} />
                </Stack>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 3, width: '100%' }}>
                <Button variant="outlined" sx={{ flex: 1, minWidth: 0 }} onClick={() => navigate('/home')} fullWidth={isXs}>Volver al inicio</Button>
              </Stack>
            </Stack>
          )}
        </Paper>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Comprobante #{params.paymentId}. Simulación (sin cargo real).
          </Typography>
        </Box>
      </PageContainer>
    </Box>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={0.5} sx={{ gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, textAlign: { xs: 'left', sm: 'right' }, wordBreak: 'break-word' }}>{value}</Typography>
    </Stack>
  );
}
